import { Link } from 'react-router-dom';
import type { Competitor, FeatureClaim, FeatureDefinition } from './types';

const LABELS: Record<FeatureClaim['value'], string> = {
  yes: 'Yes',
  no: 'No',
  partial: 'Partly',
  unknown: 'Not verified',
};

function tone(claim: FeatureClaim, inverted?: boolean) {
  if (claim.value === 'partial') return 'text-amber-700';
  if (claim.value === 'unknown') return 'text-slate-400';
  const good = inverted ? claim.value === 'no' : claim.value === 'yes';
  return good ? 'text-emerald-700' : 'text-slate-500';
}

function icon(value: FeatureClaim['value'], inverted?: boolean) {
  if (value === 'yes') return inverted ? '✕' : '✓';
  if (value === 'no') return inverted ? '✓' : '✕';
  if (value === 'partial') return '◐';
  return '?';
}

function isExternal(url: string) {
  return /^https?:\/\//.test(url);
}

function Cell({ claim, inverted, vendor }: { claim?: FeatureClaim; inverted?: boolean; vendor: string }) {
  const value = claim ?? { value: 'unknown' as const };
  return (
    <td className="block min-w-0 px-4 pb-4 pt-1 align-top sm:table-cell sm:border-t sm:border-slate-200 sm:py-3">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500 sm:hidden">{vendor}</span>
      <span className={`inline-flex items-center gap-1.5 font-semibold ${tone(value, inverted)}`}>
        <span aria-hidden="true">{icon(value.value, inverted)}</span>
        {value.badge ?? LABELS[value.value]}
      </span>
      {value.note && <p className="mt-1 text-sm leading-snug text-slate-600 break-words">{value.note}</p>}
      {value.sourceUrl && (
        <a
          href={value.sourceUrl}
          {...(isExternal(value.sourceUrl) ? { target: '_blank', rel: 'noopener noreferrer nofollow' } : {})}
          className="mt-1 inline-block py-1 text-xs text-slate-500 underline underline-offset-2 hover:text-[#1a4e94]"
        >
          source
        </a>
      )}
    </td>
  );
}

interface ComparisonTableProps {
  ours: Competitor;
  theirs: Competitor;
  features: FeatureDefinition[];
  caption?: string;
}

export default function ComparisonTable({ ours, theirs, features, caption }: ComparisonTableProps) {
  return (
    // Below 640px each feature stacks: its name on top, then the two values side by side.
    <div className="rounded-2xl border border-slate-200 sm:overflow-x-auto">
      <table className="block w-full border-collapse text-left sm:table sm:min-w-[640px]">
        <caption className="sr-only">{caption ?? 'Feature comparison'}: {ours.name} and {theirs.name}</caption>
        <thead className="hidden bg-slate-50 sm:table-header-group">
          <tr>
            <th scope="col" className="w-1/4 px-4 py-3 text-sm font-semibold text-slate-500">Feature</th>
            <th scope="col" className="px-4 py-3 text-sm font-semibold text-slate-900">{ours.name}</th>
            <th scope="col" className="px-4 py-3 text-sm font-semibold text-slate-900">{theirs.name}</th>
          </tr>
        </thead>
        <tbody className="block sm:table-row-group">
          {features.map((feature) => (
            <tr key={feature.key} className="grid grid-cols-2 border-t border-slate-200 first:border-t-0 sm:table-row sm:border-t-0">
              <th scope="row" className="col-span-2 px-4 pb-2 pt-4 align-top sm:table-cell sm:border-t sm:border-slate-200 sm:py-3">
                {feature.href
                  ? <Link to={feature.href} className="font-semibold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:text-[#1a4e94]">{feature.label}</Link>
                  : <span className="font-semibold text-slate-900">{feature.label}</span>}
                <p className="mt-1 text-sm font-normal leading-snug text-slate-500">{feature.description}</p>
              </th>
              <Cell claim={ours.features[feature.key]} inverted={feature.inverted} vendor={ours.name} />
              <Cell claim={theirs.features[feature.key]} inverted={feature.inverted} vendor={theirs.name} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
